/*!
 * Oneskin Collection Banner — vanilla JS controller for the collection page
 * banner section (reveal animation only; the banner has no interactive state).
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'collection-banner.js' | asset_url }}" defer="defer"></script>
 *
 * Replaces:
 *   frontend/scripts/templates/collection-banner.js
 *   frontend/scripts/lib/animation/collection-banner.js
 *
 * Preserves all existing data-attribute/class hooks
 * (`[data-section-type="collection-banner"]`, `.collection-banner__image-container`,
 * `.collection-banner__text-container-inner`, `.animation`/`.is-visible`) so no
 * Liquid markup changes were required beyond the scoped <style> + <script> tags.
 */
;(() => {
  'use strict'

  // A page can hold more than one banner, and each one emits its own <script src>
  // tag — identical tags still execute once per tag.
  if (window.__oneskinCollectionBanner) return
  window.__oneskinCollectionBanner = true

  const selectors = {
    image: '.collection-banner__image-container',
    content: '.collection-banner__text-container-inner > *',
  }

  function getMediaQuery(size) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(`--media-${size}`)
    return value || false
  }

  function matchesMedia(size) {
    const query = getMediaQuery(size)
    return query ? window.matchMedia(query).matches : false
  }

  function shouldAnimate(node) {
    return node.classList.contains('animation') && !document.documentElement.classList.contains('prefers-reduced-motion')
  }

  function delayOffset(node, selectorList, offsetStart = 0) {
    let offset = offsetStart
    selectorList.forEach((selector) => {
      node.querySelectorAll(selector).forEach((item) => {
        item.style.setProperty('--delay-offset-multiplier', offset)
        offset++
      })
    })
  }

  function intersectionWatcher(node) {
    const margin = matchesMedia('above-720') ? 200 : 100
    const threshold = Math.min(margin / (node.offsetHeight || 1), 0.5)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add('is-visible')
          observer.disconnect()
        }
      },
      { threshold }
    )
    observer.observe(node)

    return {
      destroy() {
        observer.disconnect()
      },
    }
  }

  function initCollectionBanner(container) {
    if (!shouldAnimate(container)) return { unload() {} }

    delayOffset(container, [selectors.image, selectors.content])
    const animation = intersectionWatcher(container)

    return {
      unload() {
        animation.destroy()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Auto init / theme editor section lifecycle
   * ---------------------------------------------------------------------- */

  const instances = new WeakMap()

  function initContainer(container) {
    if (instances.has(container)) return
    instances.set(container, initCollectionBanner(container))
  }

  function unloadContainer(container) {
    instances.get(container)?.unload?.()
    instances.delete(container)
  }

  function autoInit(root = document) {
    root.querySelectorAll('[data-section-type="collection-banner"]').forEach(initContainer)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit())
  } else {
    autoInit()
  }

  function containerFor(event) {
    const sectionId = event.detail?.sectionId
    return sectionId ? document.querySelector(`[data-section-id="${sectionId}"]`) : null
  }

  document.addEventListener('shopify:section:load', (event) => {
    const container = containerFor(event)
    if (container) initContainer(container)
    else autoInit(event.target)
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const container = containerFor(event)
    if (container) unloadContainer(container)
  })
})()
